import { Upload, Button, message } from 'antd';
import { InboxOutlined, UploadOutlined } from '@ant-design/icons';
import type { UploadProps } from 'antd';

const { Dragger } = Upload;

interface FileUploadProps {
  accept?: string;
  maxSize?: number; // in MB
  maxCount?: number;
  dragger?: boolean;
  onUpload: (file: File) => void | Promise<void>;
  onChange?: (fileList: File[]) => void;
  disabled?: boolean;
}

export function FileUpload({
  accept = '.xlsx,.xls,.pdf,.html',
  maxSize = 10,
  maxCount = 1,
  dragger = true,
  onUpload,
  onChange,
  disabled = false,
}: FileUploadProps) {
  const beforeUpload = (file: File) => {
    // Check file size
    const isValidSize = file.size / 1024 / 1024 < maxSize;
    if (!isValidSize) {
      message.error(`File must be smaller than ${maxSize}MB`);
      return Upload.LIST_IGNORE;
    }

    // Check file type
    const acceptedTypes = accept.split(',').map((t) => t.trim());
    const fileExtension = `.${file.name.split('.').pop()?.toLowerCase()}`;
    const isValidType = acceptedTypes.some((type) => {
      if (type.startsWith('.')) {
        return fileExtension === type;
      }
      return file.type.includes(type);
    });

    if (!isValidType) {
      message.error(`Only ${acceptedTypes.join(', ')} files are accepted`);
      return Upload.LIST_IGNORE;
    }

    return false; // Prevent auto upload
  };

  const uploadProps: UploadProps = {
    accept,
    maxCount,
    multiple: maxCount > 1,
    beforeUpload,
    disabled,
    customRequest: ({ file, onSuccess }) => {
      // Handle the file upload
      if (file instanceof File) {
        onUpload(file);
        onSuccess?.('ok');
      }
    },
    onChange: (info) => {
      if (onChange) {
        const files = info.fileList
          .filter((f) => f.originFileObj)
          .map((f) => f.originFileObj as File);
        onChange(files);
      }
    },
  };

  if (dragger) {
    return (
      <Dragger {...uploadProps} style={{ padding: '20px' }}>
        <p className="ant-upload-drag-icon">
          <InboxOutlined style={{ fontSize: '48px', color: '#1890ff' }} />
        </p>
        <p className="ant-upload-text" style={{ fontSize: '16px', margin: '16px 0 8px' }}>
          Click or drag file to this area to upload
        </p>
        <p className="ant-upload-hint" style={{ color: '#8c8c8c' }}>
          Support for {accept.split(',').join(', ')} files.
          {maxSize && ` Maximum file size: ${maxSize}MB.`}
        </p>
      </Dragger>
    );
  }

  return (
    <Upload {...uploadProps}>
      <Button icon={<UploadOutlined />} disabled={disabled}>
        Select File
      </Button>
    </Upload>
  );
}
